/**
 * Anwendertaugliche Kurzfassung einer VARIO-API-Fehlermeldung.
 *
 * **Warum es das braucht.** Die Worker- und Edge-Function-Fehler tragen den VARIO-Antwortkörper
 * roh weiter. In der Oberfläche stand dann so etwas — und weil der Text vorher noch abgeschnitten
 * wurde, hörte er mitten im JSON auf (Betreiber-Befund 28.08.2026):
 *
 * ```
 * Inventur fehlgeschlagen: POST /wms/inventories 403: {"instance":"/api/vario/wms/inventories",
 * "trackingId":"1231deaf-…","errors":{"fieldErrors":{},"services":[{"message":{"messageCode":
 * "msg_unauthorized_missing_right","messageParameters":{"verb":"create","resource":"inventory"},
 * "formattedMessage":"User has insufficient rights for Method inventory, operation create"}}]},"decisi
 * ```
 *
 * Brauchbar daran ist genau ein Satz: der App fehlt ein Recht. `describeVarioError` holt ihn
 * heraus. Der technische Wortlaut geht NICHT verloren — er gehört ins Fehler-Modal als
 * kopierbares JSON, nur nicht in die Fläche.
 *
 * Analog zu `gambio-error-text.ts`, nur für die andere Seite.
 */

export interface VarioErrorDescription {
  /** Kurzer Satz für die Oberfläche. */
  text: string;
  /** true = der App fehlt ein Recht im Vario-Manifest. Dann hilft nur ein Manifest-Bump. */
  isMissingRight: boolean;
  /** Vario-Kennung des Vorgangs, falls vorhanden — damit man beim Support danach fragen kann. */
  trackingId: string | null;
}

/** JSON-Teil einer Meldung wie `POST /x 403: {…}` herauslösen. */
function json(raw: string): Record<string, unknown> | null {
  const start = raw.indexOf('{');
  if (start < 0) return null;
  try {
    const v: unknown = JSON.parse(raw.slice(start));
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Alles vor dem JSON — z. B. `POST /wms/inventories 403`. */
function kopf(raw: string): string {
  const start = raw.indexOf('{');
  const cut = (start > 0 ? raw.slice(0, start) : raw).trim().replace(/[:\s]+$/, '');
  return cut;
}

interface VarioMessage {
  messageCode?: unknown;
  messageParameters?: Record<string, unknown>;
  formattedMessage?: unknown;
}

/** Alle `message`-Objekte aus `errors.services[]` und `errors.fieldErrors{}` einsammeln. */
function meldungen(body: Record<string, unknown>): VarioMessage[] {
  const errors = body['errors'];
  if (!errors || typeof errors !== 'object') return [];
  const e = errors as { services?: unknown; fieldErrors?: unknown };
  const out: VarioMessage[] = [];
  if (Array.isArray(e.services)) {
    for (const s of e.services) {
      const m = (s as { message?: unknown })?.message;
      if (m && typeof m === 'object') out.push(m as VarioMessage);
    }
  }
  if (e.fieldErrors && typeof e.fieldErrors === 'object') {
    for (const liste of Object.values(e.fieldErrors as Record<string, unknown>)) {
      if (!Array.isArray(liste)) continue;
      for (const f of liste) {
        const m = (f as { message?: unknown })?.message;
        if (m && typeof m === 'object') out.push(m as VarioMessage);
      }
    }
  }
  return out;
}

const FEHLENDES_RECHT = 'msg_unauthorized_missing_right';

export function describeVarioError(raw: string | null | undefined): VarioErrorDescription {
  const text0 = (raw ?? '').trim();
  if (!text0) return { text: 'Unbekannter Fehler.', isMissingRight: false, trackingId: null };

  const body = json(text0);
  if (!body) return { text: text0, isMissingRight: false, trackingId: null };

  const tracking = typeof body['trackingId'] === 'string' ? (body['trackingId'] as string) : null;
  const liste = meldungen(body);

  // Fehlendes Recht hat Vorrang: das ist der einzige Fall, den man nicht durch andere Eingaben
  // löst, sondern nur durch ein erweitertes App-Manifest.
  const recht = liste.find((m) => m.messageCode === FEHLENDES_RECHT);
  if (recht) {
    const p = recht.messageParameters ?? {};
    const resource = typeof p['resource'] === 'string' ? p['resource'] : null;
    const verb = typeof p['verb'] === 'string' ? p['verb'] : null;
    const was = resource && verb ? ` (${resource}/${verb})` : resource ? ` (${resource})` : '';
    return {
      // Der Händler kann hier nichts tun: Das Recht kommt über das Manifest, und das liest nur der
      // Entwickler im Admin-Center neu ein. VARIO fragt den Händler danach nicht um Zustimmung
      // (Beleg Veloconnect 0.14.0 bei Cube Store Porta, 30.09.2026).
      text: `Der App fehlt in VARIO ein Recht${was}. Das lässt sich nicht in den Einstellungen `
        + 'lösen. Wir nehmen das Recht mit dem nächsten Update der App auf, danach genügt es, '
        + 'die Seite mit F5 neu zu laden.',
      isMissingRight: true,
      trackingId: tracking,
    };
  }

  const satz = liste.map((m) => (typeof m.formattedMessage === 'string' ? m.formattedMessage : null))
    .filter((x): x is string => !!x)[0];
  if (satz) return { text: satz, isMissingRight: false, trackingId: tracking };

  // Kein verwertbarer Satz — dann wenigstens Methode und Status statt einer JSON-Wand.
  const k = kopf(text0);
  return { text: k || text0, isMissingRight: false, trackingId: tracking };
}
